export const UNIT_AMOUNT_SCALE = 9;

const MAX_ATOMIC_DIGITS = 60;
const CANONICAL_NONNEGATIVE_ATOMIC_PATTERN = /^(?:0|[1-9][0-9]*)$/;
const CANONICAL_UNIT_INPUT_PATTERN = /^(?:0|[1-9][0-9]*)(?:\.[0-9]{1,9})?$/;
const ATOMIC_UNITS_PER_UNIT = 1_000_000_000n;
const DISPLAY_MINOR_UNITS_PER_UNIT = 100n;
const DISPLAY_ROUNDING_INCREMENT = ATOMIC_UNITS_PER_UNIT / 2n;
const CONTROL_CHARACTERS =
  /[\u0000-\u001f\u007f-\u009f\u061c\u200e\u200f\u202a-\u202e\u2066-\u2069]/;

export function isCanonicalNonnegativeAtomic(value) {
  return (
    typeof value === "string" &&
    value.length <= MAX_ATOMIC_DIGITS &&
    CANONICAL_NONNEGATIVE_ATOMIC_PATTERN.test(value)
  );
}

export function parseUnitAmountInputToAtomic(value, { allowZero = false } = {}) {
  if (
    typeof value !== "string" ||
    value !== value.trim() ||
    !CANONICAL_UNIT_INPUT_PATTERN.test(value)
  ) {
    throw new TypeError("A canonical decimal Unit amount is required.");
  }
  const [whole, fraction = ""] = value.split(".");
  const atomic = (
    BigInt(whole) * ATOMIC_UNITS_PER_UNIT +
    BigInt(fraction.padEnd(UNIT_AMOUNT_SCALE, "0") || "0")
  ).toString();
  if (
    atomic.length > 40 ||
    (!allowZero && atomic === "0") ||
    !isCanonicalNonnegativeAtomic(atomic)
  ) {
    throw new TypeError("A canonical decimal Unit amount is required.");
  }
  return atomic;
}

export function formatAtomicCurrency({
  amountAtomic,
  amountScale,
  currencySymbol,
}) {
  if (
    !isCanonicalNonnegativeAtomic(amountAtomic) ||
    amountScale !== UNIT_AMOUNT_SCALE ||
    typeof currencySymbol !== "string" ||
    currencySymbol !== currencySymbol.trim() ||
    currencySymbol.length === 0 ||
    currencySymbol.length > 16 ||
    Array.from(currencySymbol).length > 8 ||
    CONTROL_CHARACTERS.test(currencySymbol)
  ) {
    throw new TypeError("A canonical Unit amount and currency are required.");
  }

  const roundedMinorUnits =
    (BigInt(amountAtomic) * DISPLAY_MINOR_UNITS_PER_UNIT +
      DISPLAY_ROUNDING_INCREMENT) /
    ATOMIC_UNITS_PER_UNIT;
  const wholeUnits = roundedMinorUnits / DISPLAY_MINOR_UNITS_PER_UNIT;
  const fractionalUnits = (
    roundedMinorUnits % DISPLAY_MINOR_UNITS_PER_UNIT
  )
    .toString()
    .padStart(2, "0");

  return `${currencySymbol} ${wholeUnits.toString()}.${fractionalUnits}`;
}

// The whole amount with no rounding, for confirming a payment: at least two
// decimal places, and further places only while they carry a digit.
export function formatAtomicCurrencyExact({
  amountAtomic,
  amountScale,
  currencySymbol,
}) {
  // Reuses the canonical validation of the rounded formatter.
  formatAtomicCurrency({ amountAtomic, amountScale, currencySymbol });
  const atomic = BigInt(amountAtomic);
  const whole = (atomic / ATOMIC_UNITS_PER_UNIT).toString();
  const fraction = (atomic % ATOMIC_UNITS_PER_UNIT)
    .toString()
    .padStart(UNIT_AMOUNT_SCALE, "0")
    .replace(/0{1,7}$/, "");
  return `${currencySymbol} ${whole}.${fraction}`;
}

// Turns what a person types into an amount field into atomic units. Accepts a
// decimal comma and surplus zeros; never passes through Number.
export function parseUnitAmountDraft(value) {
  const text = typeof value === "string" ? value.trim().replace(",", ".") : "";
  if (text === "") return Object.freeze({ atomic: null, error: "empty" });
  if (!/^[0-9]*\.?[0-9]*$/.test(text) || !/[0-9]/.test(text)) {
    return Object.freeze({ atomic: null, error: "invalid" });
  }
  const [wholeDraft, fractionDraft = ""] = text.split(".");
  const fraction = fractionDraft.replace(/0+$/, "");
  if (fraction.length > UNIT_AMOUNT_SCALE) {
    return Object.freeze({ atomic: null, error: "precision" });
  }
  const whole = wholeDraft.replace(/^0+(?=[0-9])/, "") || "0";
  const canonical = fraction ? `${whole}.${fraction}` : whole;
  if (/^0(?:\.0*)?$/.test(canonical)) {
    return Object.freeze({ atomic: null, error: "zero" });
  }
  try {
    return Object.freeze({
      atomic: parseUnitAmountInputToAtomic(canonical),
      error: null,
    });
  } catch (_error) {
    return Object.freeze({ atomic: null, error: "invalid" });
  }
}
